import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { Industry } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  type CreateProjectFormData,
  createProjectFormSchema,
} from "Projects/Forms/createProjectFormSchema";
import { ProjectForm } from "Projects/Forms/ProjectForm";
import { BlModal } from "Shared/Dialogs/BlModal";

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (projectData: CreateProjectFormData) => void | Promise<void>;
  serverError?: Error | null;
  preselectedModuleID?: string;
}

export const CreateProjectModal = ({
  isOpen,
  onClose,
  onConfirm,
  serverError,
  preselectedModuleID,
}: CreateProjectModalProps): React.ReactNode => {
  const { t } = useResource();

  const form = useForm<CreateProjectFormData>({
    resolver: zodResolver(createProjectFormSchema),
    defaultValues: {
      name: "",
      clientID: "",
      moduleID: "",
      industry: Industry.Retail,
      language: "",
      licensedCountries: [],
      jobsLimit: 0,
      startDate: undefined,
      endDate: undefined,
      hasExclusiveContracts: false,
      exclusiveContracts: [],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = form;

  useEffect(() => {
    if (isOpen && preselectedModuleID) {
      setValue("moduleID", preselectedModuleID);
    }
  }, [isOpen, preselectedModuleID, setValue]);

  useHandleServerError({
    form,
    error: serverError,
    formKey: "name",
    resource: Resources.ApiErrors.SaveProject,
  });

  const onSubmit = async (data: CreateProjectFormData) => {
    try {
      await onConfirm(data);
      reset();
      onClose();
    } catch {
      // field errors shown in form, non-field errors as toast via useHandleServerError
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Projects.CreateProjectModal.Title)}
      onCancel={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Projects.CreateProjectModal.Create)}
      maxWidth="800px"
    >
      <ProjectForm
        control={control}
        errors={errors}
        autoSelectFirstModule={!preselectedModuleID}
        setValue={setValue}
        disableModule={!!preselectedModuleID}
      />
    </BlModal>
  );
};
